import type { AxiosRequestConfig, ResponseType } from 'axios';
import axios from 'axios';
import { loadAnalysesList, loadAnalysis, uploadAnalysis } from './analyses';
import type { HttpResponse, Response } from './ClientResponse';
import {
  ClientResponse, ClientStatus,
} from './ClientResponse';
import { loadCorpus, loadShowcaseCorpus } from './corpus';
import { folderFormatToArray } from './folder';
import type { IClient } from './IClient';
import type { User } from './User';
import { login, changePassword } from './User';
import { loadPiece, uploadSync } from './piece';
import { loadCorpusData } from './corpusData';

export class Client implements IClient {
  user: User | null = null;

  private get config(): AxiosRequestConfig {
    const headers: Record<string, string> = {};
    if (this.user) headers.authorization = `Bearer ${this.user.token}`;
    return {
      headers,
      timeout: 20 * 1000,
    };
  }

  baseUrl: string;

  staticRoute: string;

  authUrl: string | null;

  changePwdUrl: string | null;

  isLocalClient: boolean;

  isIllegalUrlComponent(str: string): boolean {
    return !!str.match(/[^A-z0-9-_/.: ]/);
  }

  sourceUrl(url: string, pieceId?: string): Response<string> {
    if (this.isAbsoluteUrl(url)) {
      return this.buildUrl(url);
    }
    return this.buildUrl(`${this.staticRoute}/${pieceId}`, url);
  }

  pieceUrl(path: string): Response<string> {
    let pieceUrl;
    if (this.isLocalClient) {
      pieceUrl = this.buildUrl(path, '/info.json').data;
    } else if (this.isAbsoluteUrl(path)) {
      pieceUrl = this.buildUrl(path).data;
    } else {
      pieceUrl = `${this.baseUrl}corpus/${path}?static=true`;
    }
    return ClientResponse.fromData(pieceUrl);
  }

  analysisUrl(piecePath : string, analysisId : string) : Response<string> {
    let analysisResponse : Response<string>;
    if (this.isAbsoluteUrl(analysisId)) {
      analysisResponse = this.buildUrl(analysisId);
    } else if (this.isLocalClient) {
      analysisResponse = this.buildUrl(piecePath, 'analyses', analysisId);
    } else {
      analysisResponse = this.buildUrl('corpus', piecePath, 'analyses', analysisId);
    }

    return analysisResponse;
  }

  isAbsoluteUrl(url : string) : boolean {
    // from https://www.geeksforgeeks.org/javascript/javascript-check-whether-a-url-string-is-absolute-or-relative/
    const RgExp = /^(?:[a-z]+:)?\/\//i;
    return RgExp.test(url);
  }

  /*
    gitlab.com has a strict CORS policy so files must be requested through the public api
    Changes url from something like
    https://gitlab.com/algomus.fr/dezrann/dezrann-corpus/-/raw/main/metadata/test/fugue-bwv862/score-positions.json
    to
    https://gitlab.com/api/v4/projects/algomus.fr%2Fdezrann%2Fdezrann-corpus/repository/files/metadata%2Ftest%2Ffugue-bwv862%2Fscore-positions.json/raw
    doc : https://docs.gitlab.com/api/repository_files/
    */
  formatUrlForGitlabApi(url : string) : string {
    if (url.includes('gitlab.com') && !url.includes('/api/')) {
      const raw = url.split('/raw/')[1].split('/');
      const branch = raw[0];
      const filePath = raw.slice(1).join('/').replaceAll('/', '%2F');
      const projectId = url.split('://gitlab.com/')[1].split('/-/')[0].replaceAll('/', '%2F');
      const apiUrl = `https://gitlab.com/api/v4/projects/${projectId}/repository/files/${filePath}/raw?ref=${branch}`;
      return apiUrl;
    }
    return url;
  }

  /**
   * Builds a URL from a list of string  and returns a typed Response (isOk, isErr…).
   *
   * The function validates the components (illegal characters, empty segments)
   * before constructing the final URL.
   *
   * If the constructed URL is absolute, it is formatted for the GitLab API
   *
   * Otherwise, it is prefixed with this.baseUrl.
   */
  buildUrl(...components: string[]): Response<string> {
    // Check if any illegal characters were passed to the url
    const illegalIndex = components.findIndex(this.isIllegalUrlComponent);
    if (illegalIndex >= 0) {
      return new ClientResponse(
        ClientStatus.Missing,
        new Error(`Illegal character in url: '${components[illegalIndex]}'`),
      ).cast();
    }
    // Check if any url components are empty
    const emptyIndex = components.findIndex((c) => !c);
    if (emptyIndex !== -1 && emptyIndex !== components.length - 1) {
      return ClientResponse.fromParsingError(new Error('Empty url component found in url, and not at the end'));
    }
    if (this.isAbsoluteUrl(components.join('/'))) {
      let url = components.join('/');
      url = this.formatUrlForGitlabApi(url);
      return ClientResponse.fromData(url);
    }

    return ClientResponse.fromData(this.baseUrl + components.join('/'));
  }

  async get<T>(url: string, responseType:ResponseType = 'json'): Promise<HttpResponse<T>> {
    try {
      const typedConfig = this.config;
      typedConfig.responseType = responseType;
      const response = await axios.get<T>(url, typedConfig);
      const { data } = response;
      return ClientResponse.fromData(data);
    } catch (e) {
      if (axios.isAxiosError(e)) return ClientResponse.fromAxios<T>(e);
      throw e;
    }
  }

  async getFolder(url: string): Promise<Response<string[]>> {
    try {
      const response = await axios.get<unknown>(url, this.config);
      const { data } = response;
      return folderFormatToArray(data);
    } catch (e) {
      if (axios.isAxiosError(e)) return ClientResponse.fromAxios<string[]>(e);
      if (ClientResponse.isClientError(e)) return e as HttpResponse<string[]>;
      throw e;
    }
  }

  async post<T>(
    url: string,
    postData: unknown,
    config:AxiosRequestConfig | null = null,
  ): Promise<HttpResponse<T>> {
    try {
      const response = await axios.post<T>(url, postData, { ...this.config, ...config });
      const { data } = response;
      return ClientResponse.fromData(data);
    } catch (e) {
      if (axios.isAxiosError(e)) return ClientResponse.fromAxios<T>(e);
      throw e;
    }
  }

  async put<T>(url: string, postData: unknown): Promise<HttpResponse<T>> {
    try {
      const response = await axios.put<T>(url, postData, this.config);
      const { data } = response;
      return ClientResponse.fromData(data);
    } catch (e) {
      if (axios.isAxiosError(e)) return ClientResponse.fromAxios<T>(e);
      throw e;
    }
  }

  tryParse<T>(fn: () => T): Response<T> {
    try {
      return ClientResponse.fromData(fn());
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed Parsing:', error);
      if (ClientResponse.isClientError<T>(error)) return error;
      return ClientResponse.fromParsingError<T>(error as Error);
    }
  }

  get canLogin(): boolean {
    return !!this.login;
  }

  get canUpload(): boolean {
    return !this.isLocalClient;
  }

  loadPiece = loadPiece.bind(this);

  loadCorpus = loadCorpus.bind(this);

  loadCorpusData = loadCorpusData.bind(this);

  loadShowcaseCorpus = loadShowcaseCorpus.bind(this);

  loadAnalysesList = loadAnalysesList.bind(this);

  loadAnalysis = loadAnalysis.bind(this);

  uploadAnalysis = uploadAnalysis.bind(this);

  uploadSync = uploadSync.bind(this);

  login = login.bind(this);

  changePassword = changePassword.bind(this);

  constructor(
    baseUrl = 'https://ws.dezrann.net/',
    authUrl: string | null = null,
    changePwdUrl: string | null = null,
    isLocalClient = false,
    staticRoute = '',
    user: User | null = null,
  ) {
    this.baseUrl = baseUrl;
    this.authUrl = authUrl;
    this.changePwdUrl = changePwdUrl;
    this.isLocalClient = isLocalClient;
    this.staticRoute = staticRoute;
    this.user = user;
  }
}
